import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  User,
  Shield,
  Download,
  Trash2,
  CreditCard,
  Sparkles,
  ExternalLink,
  AlertTriangle,
  Loader2,
  Mail,
  Info,
  ScrollText,
  KeyRound,
  ShieldAlert,
  ShieldCheck,
  ShieldX,
  FileDown,
  Bell,
  Clock,
  ChevronDown
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Separator } from '../components/ui/separator';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../components/ui/dialog';
import { useUser } from '@clerk/clerk-react';
import { useLifely } from '../context/LifelyContext';
import { usePlan } from '../context/PlanContext';
import { accountAPI, activityAPI, stripeAPI } from '../services/api';
import UpgradeModal from '../components/billing/UpgradeModal';
import { toast } from 'sonner';
import { APP_VERSION } from '../config/version';

// Security event display config (static, outside component)
const SECURITY_EVENTS = {
  emergency_code_generated: { icon: KeyRound, label: 'Emergency code generated', color: 'text-blue-600' },
  emergency_code_revoked: { icon: ShieldAlert, label: 'Emergency code revoked', color: 'text-amber-600' },
  emergency_access_requested: { icon: ShieldAlert, label: 'Emergency access requested', color: 'text-red-600' },
  emergency_access_denied: { icon: ShieldX, label: 'Access request denied', color: 'text-green-600' },
  emergency_access_approved: { icon: ShieldCheck, label: 'Access granted (48h expired)', color: 'text-red-600' },
  nominee_export_accessed: { icon: FileDown, label: 'Nominee accessed export', color: 'text-amber-600' },
  check_in_confirmed: { icon: ShieldCheck, label: 'Check-in confirmed', color: 'text-green-600' },
  reminder_sent: { icon: Bell, label: 'Check-in reminder sent', color: 'text-blue-600' },
  escalation_triggered: { icon: ShieldAlert, label: 'Escalation triggered', color: 'text-red-600' },
  will_reminder_sent: { icon: Bell, label: 'Will review reminder sent', color: 'text-blue-600' },
};

const Settings = () => {
  const { user } = useLifely();
  const { user: clerkUser } = useUser();
  const { planName, hasFullAccess, isTrial, isExpired, hasStandardPlan, hasPlusPlan } = usePlan();
  const [showUpgrade, setShowUpgrade] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  // Carry over "Get started" intent from the pricing page: once plan data has
  // loaded (avoids a false read during the post-signup auth sync), auto-open
  // the upgrade modal for ?upgrade=standard, then strip the param so a
  // refresh doesn't reopen it.
  useEffect(() => {
    if (!planName) return;
    const params = new URLSearchParams(location.search);
    if (params.get('upgrade') !== 'standard') return;

    if (!hasStandardPlan && !hasPlusPlan) {
      setShowUpgrade(true);
    }
    params.delete('upgrade');
    navigate({ pathname: location.pathname, search: params.toString() }, { replace: true });
  }, [planName, hasStandardPlan, hasPlusPlan, location.search, location.pathname, navigate]);

  // Days remaining on a trial (if any)
  const trialDaysRemaining = (() => {
    if (!isTrial || !user?.trialExpires) return null;
    const ms = new Date(user.trialExpires).getTime() - Date.now();
    if (Number.isNaN(ms)) return null;
    return Math.max(0, Math.ceil(ms / (24 * 60 * 60 * 1000)));
  })();

  // Open the Stripe billing portal; fall back to the upgrade modal if no customer exists.
  const handleManageBilling = async () => {
    try {
      const { url } = await stripeAPI.createPortalSession();
      if (url) {
        window.location.href = url;
      } else {
        setShowUpgrade(true);
      }
    } catch (err) {
      setShowUpgrade(true);
    }
  };

  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleteStep, setDeleteStep] = useState(1);
  const [isDeleting, setIsDeleting] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [securityLog, setSecurityLog] = useState([]);
  const [logLoading, setLogLoading] = useState(true);
  const [showAllLogs, setShowAllLogs] = useState(false);

  // Fetch security log
  useEffect(() => {
    const fetchLog = async () => {
      try {
        const data = await activityAPI.list({ limit: 50 });
        // Filter to security-relevant events only
        const securityEvents = (data.activities || []).filter(
          a => SECURITY_EVENTS[a.action]
        );
        setSecurityLog(securityEvents);
      } catch (err) {
        console.error('Failed to fetch security log:', err);
      } finally {
        setLogLoading(false);
      }
    };
    fetchLog();
  }, []);
  
  // Format date helper
  const formatDate = (dateString) => {
    if (!dateString) return '-';
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch {
      return '-';
    }
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return '-';
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
      });
    } catch {
      return '-';
    }
  };

  const getEventDetail = (activity) => {
    const details = activity.details;
    if (!details) return null;
    switch (activity.action) {
      case 'emergency_access_requested':
        return `by ${details.requester_name || details.requester_email || 'unknown'}`;
      case 'emergency_access_denied':
        return `request from ${details.requester_name || 'unknown'}${details.method === 'email_link' ? ' (via email)' : ''}`;
      case 'emergency_code_revoked':
        return details.reason === 'manual_revocation' ? '(manual)' : '(after request)';
      case 'check_in_confirmed':
        return details.label || null;
      default:
        return null;
    }
  };
  
  const accountCreated = clerkUser?.createdAt || user?.createdAt;
  const lastLogin = user?.lastLogin || null;

  const firstName = clerkUser?.firstName || user?.firstName || '';
  const lastName = clerkUser?.lastName || user?.lastName || '';
  const displayName = clerkUser?.fullName || (firstName && lastName ? `${firstName} ${lastName}` : firstName) || user?.email?.split('@')[0] || 'User';
  const displayEmail = clerkUser?.primaryEmailAddress?.emailAddress || user?.email || '';
  const displayInitial = (firstName || displayName).charAt(0).toUpperCase();
  const avatarUrl = clerkUser?.imageUrl || null;

  const handleDeleteRequest = async () => {
    try {
      setIsDeleting(true);
      await accountAPI.requestDeletion();
      setEmailSent(true);
      setDeleteStep(3);
      toast.success('Confirmation email sent. Please check your inbox.');
    } catch (err) {
      const serverMessage = err?.response?.data?.message;
      toast.error(serverMessage || 'Failed to send confirmation email. Please try again.');
      console.error('Delete request error:', err);
    } finally {
      setIsDeleting(false);
    }
  };

  const resetDeleteDialog = () => {
    setShowDeleteDialog(false);
    setDeleteStep(1);
    setEmailSent(false);
  };

  return (
    <div className="max-w-2xl mx-auto space-y-8 animate-fade-in" data-testid="settings-page">
      {/* Header */}
      <section className="space-y-2">
        <h1 className="text-3xl sm:text-4xl font-heading font-semibold text-foreground">
          Settings
        </h1>
        <p className="text-muted-foreground text-base">
          Manage your account, privacy preferences, and data.
        </p>
      </section>

      {/* Profile Section */}
      <Card className="border border-border" data-testid="profile-section">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-base font-medium font-body">
            <User className="w-4 h-4 text-primary" />
            Profile
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center overflow-hidden shrink-0">
              {avatarUrl ? (
                <img src={avatarUrl} alt={displayName} className="w-full h-full object-cover" referrerPolicy="no-referrer" />
              ) : (
                <span className="text-2xl font-heading font-semibold text-primary">
                  {displayInitial}
                </span>
              )}
            </div>
            <div>
              <p className="font-medium text-lg">{displayName}</p>
              <p className="text-sm text-muted-foreground">{displayEmail}</p>
            </div>
          </div>

          <Separator />

          <div className="space-y-3">
            {(firstName || lastName) && (
              <>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium">First name</p>
                    <p className="text-sm text-muted-foreground">{firstName || '-'}</p>
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium">Last name</p>
                    <p className="text-sm text-muted-foreground">{lastName || '-'}</p>
                  </div>
                </div>
                <Separator />
              </>
            )}
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium">Account created</p>
                <p className="text-sm text-muted-foreground">{formatDate(accountCreated)}</p>
              </div>
            </div>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium">Last login</p>
                <p className="text-sm text-muted-foreground">{formatDate(lastLogin)}</p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Privacy & Data Section */}
      <Card className="border border-border" data-testid="privacy-section">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-base font-medium font-body">
            <Shield className="w-4 h-4 text-primary" />
            Privacy & Data
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between py-2">
            <div>
              <p className="text-sm font-medium">Export your data</p>
              <p className="text-xs text-muted-foreground">
                Download a copy of all your Lifely data (GDPR)
              </p>
            </div>
            <Button variant="outline" size="sm" asChild data-testid="export-data-btn">
              <Link to="/print-export">
                <Download className="w-4 h-4 mr-1" />
                Export
              </Link>
            </Button>
          </div>

          <Separator />

          <div className="flex items-center justify-between py-2">
            <div>
              <p className="text-sm font-medium text-destructive">Delete your account</p>
              <p className="text-xs text-muted-foreground">
                Permanently delete your account and all data
              </p>
            </div>
            <Button 
              variant="outline" 
              size="sm" 
              className="text-destructive border-destructive/30 hover:bg-destructive/10" 
              data-testid="delete-account-btn"
              onClick={() => setShowDeleteDialog(true)}
            >
              <Trash2 className="w-4 h-4 mr-1" />
              Delete Account
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Plan Section */}
      <Card className="border border-border" data-testid="plan-section">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-base font-medium font-body">
            <Sparkles className="w-4 h-4 text-primary" />
            Plan
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="p-4 rounded-lg bg-muted/50 border border-border">
            <div className="flex items-center justify-between gap-2">
              <p className="font-medium text-sm">Current Plan</p>
              {hasFullAccess && !isTrial && (
                <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-primary/10 text-primary border border-primary/20">
                  Active
                </span>
              )}
            </div>
            <p className="text-2xl font-heading font-semibold mt-1">{planName || 'No Plan'}</p>
            <p className="text-xs text-muted-foreground mt-1">
              {isTrial
                ? (trialDaysRemaining !== null
                    ? `${trialDaysRemaining} ${trialDaysRemaining === 1 ? 'day' : 'days'} remaining in your trial`
                    : 'Free trial, full access')
                : isExpired
                ? 'Your trial has ended - upgrade to restore access'
                : hasFullAccess
                ? 'Full access to all maps and continuity features'
                : 'Limited access - upgrade for full features'}
            </p>
          </div>

          {isTrial || isExpired || !hasFullAccess ? (
            <Button
              className="w-full"
              data-testid="upgrade-plan-btn"
              onClick={() => setShowUpgrade(true)}
            >
              <Sparkles className="w-4 h-4 mr-2" />
              Upgrade to Standard
            </Button>
          ) : (
            <Button
              variant="outline"
              className="w-full"
              data-testid="manage-billing-btn"
              onClick={handleManageBilling}
            >
              <CreditCard className="w-4 h-4 mr-2" />
              Manage billing
            </Button>
          )}
        </CardContent>
      </Card>

      {/* Security Log Section */}
      <Card className="border border-border" data-testid="security-log-section">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-base font-medium font-body">
            <ScrollText className="w-4 h-4 text-primary" />
            Security Log
          </CardTitle>
        </CardHeader>
        <CardContent>
          {logLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
            </div>
          ) : securityLog.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-6">
              No security events recorded yet.
            </p>
          ) : (
            <div className="space-y-0">
              {(showAllLogs ? securityLog : securityLog.slice(0, 8)).map((event) => {
                const config = SECURITY_EVENTS[event.action] || { icon: Clock, label: event.action, color: 'text-muted-foreground' };
                const EventIcon = config.icon;
                const detail = getEventDetail(event);
                return (
                  <div key={event.id} className="flex items-start gap-3 py-2.5 border-b border-border last:border-0" data-testid={`security-event-${event.id}`}>
                    <EventIcon className={`w-4 h-4 mt-0.5 flex-shrink-0 ${config.color}`} />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-foreground">{config.label}</p>
                      {detail && <p className="text-xs text-muted-foreground truncate">{detail}</p>}
                    </div>
                    <span className="text-xs text-muted-foreground whitespace-nowrap">{formatDateTime(event.createdAt)}</span>
                  </div>
                );
              })}
              {securityLog.length > 8 && !showAllLogs && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="w-full mt-2 text-muted-foreground"
                  onClick={() => setShowAllLogs(true)}
                  data-testid="show-all-logs-btn"
                >
                  <ChevronDown className="w-4 h-4 mr-1" />
                  Show all ({securityLog.length} events)
                </Button>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Sign Out */}
      <div className="pt-4">
        <Button 
          variant="ghost" 
          className="w-full text-muted-foreground hover:text-foreground"
          data-testid="sign-out-btn"
        >
          Sign Out
        </Button>
      </div>

      {/* Version Info */}
      <div className="pt-4 pb-8 text-center">
        <p className="text-xs text-muted-foreground">
          Lifely v{APP_VERSION}
        </p>
      </div>

      {/* Delete Account Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={resetDeleteDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-destructive">
              <AlertTriangle className="w-5 h-5" />
              Delete Account
            </DialogTitle>
          </DialogHeader>

          {deleteStep === 1 && (
            <>
              <DialogDescription className="space-y-4 pt-2">
                <p>
                  Are you sure you want to delete your Lifely account? This action is <strong>permanent and cannot be undone</strong>.
                </p>
                
                <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-4 space-y-2">
                  <p className="font-medium text-sm text-destructive">This will permanently delete:</p>
                  <ul className="text-sm space-y-1 text-muted-foreground">
                    <li>• Your Lifely account and profile</li>
                    <li>• All maps and entries (assets, properties, digital accounts)</li>
                    <li>• All contacts and sharing settings</li>
                    <li>• Will status and formal arrangements</li>
                    <li>• Continuity settings and nominees</li>
                    <li>• Any active subscription</li>
                  </ul>
                </div>
              </DialogDescription>

              <DialogFooter className="gap-2 sm:gap-0 mt-4">
                <Button variant="outline" onClick={resetDeleteDialog}>
                  Cancel
                </Button>
                <Button 
                  variant="destructive"
                  onClick={() => setDeleteStep(2)}
                >
                  I Understand, Continue
                </Button>
              </DialogFooter>
            </>
          )}

          {deleteStep === 2 && (
            <>
              <DialogDescription className="space-y-4 pt-2">
                <p>
                  To confirm deletion, we&apos;ll send a verification email to:
                </p>
                
                <div className="bg-muted rounded-lg p-3 flex items-center gap-3">
                  <Mail className="w-5 h-5 text-muted-foreground" />
                  <span className="font-medium">{displayEmail}</span>
                </div>
                
                <p className="text-sm text-muted-foreground">
                  Click the link in the email to permanently delete your account. The link will expire in 24 hours.
                </p>
              </DialogDescription>

              <DialogFooter className="gap-2 sm:gap-0 mt-4">
                <Button variant="outline" onClick={() => setDeleteStep(1)}>
                  Back
                </Button>
                <Button 
                  variant="destructive"
                  onClick={handleDeleteRequest}
                  disabled={isDeleting}
                >
                  {isDeleting ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    'Send Confirmation Email'
                  )}
                </Button>
              </DialogFooter>
            </>
          )}

          {deleteStep === 3 && (
            <>
              <DialogDescription className="space-y-4 pt-2">
                <div className="text-center py-4">
                  <div className="w-12 h-12 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center mx-auto mb-4">
                    <Mail className="w-6 h-6 text-green-600" />
                  </div>
                  <p className="font-medium text-lg mb-2">Check Your Email</p>
                  <p className="text-muted-foreground">
                    We&apos;ve sent a confirmation link to <strong>{displayEmail}</strong>
                  </p>
                </div>
                
                <div className="bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 rounded-lg p-3">
                  <p className="text-sm text-amber-800 dark:text-amber-200">
                    <strong>Important:</strong> Your account will only be deleted after you click the confirmation link in the email. The link expires in 24 hours.
                  </p>
                </div>
              </DialogDescription>

              <DialogFooter className="mt-4">
                <Button variant="outline" onClick={resetDeleteDialog} className="w-full">
                  Close
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      <UpgradeModal isOpen={showUpgrade} onClose={() => setShowUpgrade(false)} trigger={isExpired ? 'trial_expired' : 'upgrade_prompt'} />
    </div>
  );
};

export default Settings;
